import { setGlobalLoading } from '$lib/stores/app/actions';

/**
 * Tracks how many concurrent withLoading() calls are active.
 * Loading stays on until every caller has finished — this prevents
 * sequential actions (each calling withLoading internally) from
 * flickering the overlay off between calls.
 * @type {number}
 */
let loadingCount = 0;

/**
 * @returns {void}
 */
function start() {
	loadingCount = loadingCount + 1;
	if (loadingCount === 1) setGlobalLoading(true);
}

/**
 * Stop the loading overlay. Safe to call even if loading is not active.
 * @param {boolean} [forceStop] - Force stop the loading overlay even if there are other loading operations in progress.
 * @returns {void}
 */
function stop(forceStop = false) {
	if (forceStop) loadingCount = 0;
	if (loadingCount > 0) loadingCount = loadingCount - 1;
	if (loadingCount === 0) setGlobalLoading(false);
}

/**
 * Run an async function while showing the global loading overlay. Loading is
 * started before the function runs and stopped in a finally block, so it always
 * stops even on throw or early return. Prefer this over manual start/stop.
 * @param {() => Promise<any>} fn - async function to run
 * @returns {Promise<any>} result of fn()
 */
async function withLoading(fn) {
	start();
	try {
		return await fn();
	} finally {
		stop();
	}
}

/**
 * Singleton loading service. Use withLoading() for most cases; use stop() for edge cases.
 * start() is not exposed — call withLoading() to ensure start/stop are always paired.
 */
export const loadingService = {
	stop,
	withLoading,
	/**
	 * Whether the overlay is currently shown (for tests or debugging).
	 * @returns {boolean}
	 */
	get running() {
		return loadingCount > 0;
	},
};
